import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { calcAnalytics } from '../lib/analytics'
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts'

const StatCard = ({ label, value, sub, color, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-gray-900 border border-gray-800 rounded-xl p-5 ${onClick ? 'cursor-pointer hover:border-gray-600 transition-colors' : ''}`}
  >
    <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">{label}</p>
    <p className={`text-2xl font-bold ${color || 'text-white'}`}>{value ?? '—'}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
)

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [recentTrades, setRecentTrades] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      const { data } = await supabase
        .from('trades')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: true })

      setStats(calcAnalytics(data || []))
      setRecentTrades((data || []).slice(-5).reverse())
      setLoading(false)
    }
    fetch()
  }, [user.id])

  const pnlColor = (pnl) => pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-400'

  if (loading) return <div className="p-8 text-gray-400">Loading...</div>

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">Your trading performance at a glance</p>
      </div>

      {!stats ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
          <p className="text-gray-400 text-lg mb-2">No trades yet</p>
          <p className="text-gray-600 text-sm mb-6">Start by logging a trade or importing from your exchange</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/journal')} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors">
              Log a Trade
            </button>
            <button onClick={() => navigate('/import')} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">
              Import CSV
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Scorecard */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total P&L"
              value={(stats.totalPnl > 0 ? '+' : '') + stats.totalPnl}
              sub={`${stats.totalTrades} closed trades`}
              color={stats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}
              onClick={() => navigate('/analytics')}
            />
            <StatCard
              label="Win Rate"
              value={stats.winRate + '%'}
              sub={`${stats.winners}W / ${stats.losers}L`}
              color={stats.winRate >= 50 ? 'text-green-400' : 'text-red-400'}
              onClick={() => navigate('/analytics')}
            />
            <StatCard
              label="Profit Factor"
              value={stats.profitFactor}
              sub={stats.profitFactor >= 1.5 ? '✅ Strong' : stats.profitFactor >= 1 ? '⚠️ Marginal' : '❌ Negative'}
              color={stats.profitFactor >= 1.5 ? 'text-green-400' : stats.profitFactor >= 1 ? 'text-yellow-400' : 'text-red-400'}
              onClick={() => navigate('/analytics')}
            />
            <StatCard
              label="Current Streak"
              value={stats.currentStreak}
              sub={`Best: ${stats.bestWinStreak}W / Worst: ${stats.worstLossStreak}L`}
              color={stats.currentStreak.includes('W') ? 'text-green-400' : 'text-red-400'}
              onClick={() => navigate('/analytics')}
            />
          </div>

          {/* Mini Equity Curve */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">Equity Curve</h2>
              <button onClick={() => navigate('/analytics')} className="text-blue-400 text-xs hover:text-blue-300 transition-colors">
                Full analytics →
              </button>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={stats.equityCurve}>
                <defs>
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: '#9ca3af' }}
                  itemStyle={{ color: '#60a5fa' }}
                />
                <Area type="monotone" dataKey="equity" stroke="#3b82f6" fill="url(#eq)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Trades */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-white font-semibold">Recent Trades</h2>
              <button onClick={() => navigate('/journal')} className="text-blue-400 text-xs hover:text-blue-300 transition-colors">
                View all →
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left px-5 py-3">Symbol</th>
                  <th className="text-left px-5 py-3">Direction</th>
                  <th className="text-right px-5 py-3">Entry</th>
                  <th className="text-right px-5 py-3">P&L</th>
                  <th className="text-left px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTrades.map(trade => (
                  <tr key={trade.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                    <td className="px-5 py-3 text-white font-medium">{trade.symbol}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${trade.direction === 'long' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                        {trade.direction}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-300">{trade.entry_price}</td>
                    <td className={`px-5 py-3 text-right font-medium ${pnlColor(trade.pnl)}`}>
                      {trade.pnl != null ? (trade.pnl > 0 ? '+' : '') + trade.pnl.toFixed(2) : '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-400">{new Date(trade.entry_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
